import type { App } from "vue";
import PrimeVue from "primevue/config";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import { definePreset } from "@primevue/themes";
import Aura from "@primevue/themes/aura";
import { detectLocale } from "@/plugins/i18n";
import { primeLocaleMessages } from "@/plugins/prime-locale";

const LS_THEME = "luckydesk.theme";
const DARK_CLASS = "app-dark";

// Light mode runs hot toward white: stock Aura uses very pale tokens
// (highlight = primary.50, borders = surface.200, muted text = surface.500),
// so cards, selected rows and secondary text wash out — whereas dark mode's
// translucent-accent highlights and surface.700 borders read with good
// contrast. This preset deepens ONLY the light color scheme a notch so the
// two themes carry comparable contrast; dark is left exactly as the stock
// Aura preset (the user already approved it). Tweak the magnitudes here —
// it's the single system-wide lever.
const LuckyAura = definePreset(Aura, {
  semantic: {
    colorScheme: {
      light: {
        // Selected/active surfaces (list options, menus, highlighted rows)
        // jump from near-white to a clearly tinted accent.
        highlight: {
          background: "{primary.100}",
          focusBackground: "{primary.200}",
          color: "{primary.800}",
          focusColor: "{primary.800}",
        },
        // Secondary text gets one shade darker for legibility.
        text: {
          mutedColor: "{surface.600}",
          hoverMutedColor: "{surface.700}",
        },
        // Card/panel/table borders + hover rows become visible against white.
        content: {
          borderColor: "{surface.300}",
          hoverBackground: "{surface.200}",
        },
        // Inputs get a firmer edge instead of dissolving into the page.
        formField: {
          borderColor: "{surface.400}",
          hoverBorderColor: "{surface.500}",
        },
      },
    },
  },
  components: {
    // Striped DataTables (organizations, services, users, …): stock light
    // stripes are surface.50 — a hair off white, so rows blur together. One
    // shade deeper gives the zebra effect real separation. Dark unchanged.
    datatable: {
      colorScheme: {
        light: {
          row: {
            stripedBackground: "{surface.100}",
          },
        },
      },
    },
  },
});

export type ThemeMode = "light" | "dark";

export function detectTheme(): ThemeMode {
  const saved = localStorage.getItem(LS_THEME) as ThemeMode | null;
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  if (mode === "dark") root.classList.add(DARK_CLASS);
  else root.classList.remove(DARK_CLASS);
  localStorage.setItem(LS_THEME, mode);
}

export function installPrimeVue(app: App) {
  app.use(PrimeVue, {
    theme: {
      preset: LuckyAura,
      options: {
        prefix: "p",
        darkModeSelector: `.${DARK_CLASS}`,
        cssLayer: false,
      },
    },
    // Locale inicial = idioma já escolhido pelo usuário. A TROCA em runtime é
    // feita em App.vue via usePrimeVue() (o PrimeVue faz deep-merge do locale
    // no install, então este objeto não é a referência reativa do config —
    // mutá-lo aqui não propagaria).
    locale: primeLocaleMessages(detectLocale()),
    ripple: true,
  });
  app.use(ToastService);
  app.use(ConfirmationService);
  applyTheme(detectTheme());
}
